import React from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Button,
    Stack,
    Card,
    CardContent,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Checkbox,
    Chip,
    Divider,
    IconButton,
    Tooltip,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Avatar,
    ListItemAvatar,
    TextField,
    InputAdornment,
    Collapse,
    Accordion,
    AccordionSummary,
    Switch,
    AccordionDetails
} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import {tokens} from '../../theme';
import {SubTitleScreen, TitleScreen} from '../../SystemDesign/Styles';
import {SnackBarContext} from '../../Context/SnackBarContextProvider';
import {useGetPosteCompetencyMatrixQuery, useGetPostesQuery} from '../../services/SkillsManagement';
import CompetencyMatrix from './CompetencyMatrix';
import ReportPDFExport from './ReportPDFExport';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import DeselectIcon from '@mui/icons-material/Deselect';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import GetAppIcon from '@mui/icons-material/GetApp';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const CompetencyReportsDashboard = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const {setInfoAlert, setOpenSnackBar} = React.useContext(SnackBarContext);

    // Post filter state
    const [posteIdForMatrix, setPosteIdForMatrix] = React.useState('');
    const [selectedPost, setSelectedPost] = React.useState(null);
    const [selectedUserIds, setSelectedUserIds] = React.useState([]);
    const [userSearchTerm, setUserSearchTerm] = React.useState('');
    const [userListExpanded, setUserListExpanded] = React.useState(false);
    const [showAcceptedOnly, setShowAcceptedOnly] = React.useState(true);

    // Export state
    const [exportOpen, setExportOpen] = React.useState(false);

    // Get posts for filter dropdown
    const {
        data: postsData,
        error: postsError,
        isLoading: isLoadingPosts,
        isFetching: isFetchingPosts
    } = useGetPostesQuery(
        {limit: '100', getAllWithoutPagination: true},
        {refetchOnMountOrArgChange: true}
    );

    // Format posts for selector with user count
    const postOptions = React.useMemo(() => {
        if (!postsData?.postes) return [];

        return postsData.postes.map(post => ({
            id: post.posteId,
            title: post.name || `Post #${post.posteId}`,
            userCount: post.users?.length || 0,
            color: post.color || colors.primary[500],
            rawPost: post
        }));
    }, [postsData, colors]);

    React.useEffect(() => {
        if (postsData?.postes && postsData.postes.length > 0 && !posteIdForMatrix) {
            // Find the first post that has users
            const firstPostWithUsers = postsData.postes.find(post => post.users?.length > 0);

            if (firstPostWithUsers) {
                setPosteIdForMatrix(String(firstPostWithUsers.posteId));
                setSelectedPost(firstPostWithUsers);

                // Select all users by default
                if (firstPostWithUsers.users) {
                    setSelectedUserIds(firstPostWithUsers.users.map(user => user.id));
                }
            } else if (postsData.postes.length > 0) {
                // If no posts have users, just select the first post
                const firstPost = postsData.postes[0];
                setPosteIdForMatrix(String(firstPost.posteId));
                setSelectedPost(firstPost);
                setSelectedUserIds([]);
            }
        }
    }, [postsData, posteIdForMatrix]);
    // Competency matrix query - NO employeeIds filter in API call
    const {
        data: matrixData,
        error: matrixError,
        isLoading: isLoadingMatrixLoading,
        isFetching: isFetchingMatrix,
        refetch: refetchMatrix
    } = useGetPosteCompetencyMatrixQuery(
        {
            posteId: Number(posteIdForMatrix) || 0,
            showAcceptedOnly: showAcceptedOnly
        },
        {
            skip: !posteIdForMatrix,
            refetchOnMountOrArgChange: true
        }
    );

    // Get the selected post details and reset user selection
    React.useEffect(() => {
        if (posteIdForMatrix && postsData?.postes) {
            const foundPost = postsData.postes.find(post => post.posteId === Number(posteIdForMatrix));
            setSelectedPost(foundPost || null);

            // Select all users by default when post changes
            if (foundPost?.users) {
                setSelectedUserIds(foundPost.users.map(user => user.id));
            } else {
                setSelectedUserIds([]);
                setUserListExpanded(false);
            }
        } else {
            setSelectedPost(null);
            setSelectedUserIds([]);
            setUserListExpanded(false);
        }
    }, [posteIdForMatrix, postsData]);

    React.useEffect(() => {
        if (matrixError) {
            setInfoAlert({
                severity: 'error',
                message: matrixError?.data?.message || 'An error occurred while loading the competency matrix'
            });
            setOpenSnackBar(true);
        }

        if (postsError) {
            setInfoAlert({
                severity: 'error',
                message: postsError?.data?.message || 'An error occurred while loading posts'
            });
            setOpenSnackBar(true);
        }
    }, [matrixError, postsError, setInfoAlert, setOpenSnackBar]);

    const handleExportMatrix = (type) => {
        if (type === 'PDF') {
            setExportOpen(true);
        } else if (type === 'CSV') {
            // Validate data availability
            if (!filteredMatrixData || !filteredMatrixData.employees || !filteredMatrixData.skills) {
                setInfoAlert({severity: 'error', message: 'No matrix data available for export.'});
                setOpenSnackBar(true);
                return;
            }

            try {
                // Create CSV headers
                const headers = ['Employee Name', 'Employee Email', 'Skill Name', 'Current Level', 'Required Min', 'Required Max', 'Status'];

                // Create lookup maps for efficient data access
                const employeeMap = {};
                filteredMatrixData.employees.forEach(emp => {
                    employeeMap[emp.id] = emp;
                });

                const skillMap = {};
                filteredMatrixData.skills.forEach(skill => {
                    skillMap[skill.id] = skill;
                });

                // Generate CSV rows
                const rows = [];
                filteredMatrixData.cells.forEach(cell => {
                    const employee = employeeMap[cell.employeeId];
                    const skill = skillMap[cell.skillId];

                    if (employee && skill) {
                        rows.push([
                            `"${employee.name || ''}"`,
                            `"${employee.email || 'No Email'}"`,
                            `"${skill.name || ''}"`,
                            cell.current || '',
                            skill.required_min || '',
                            skill.required_max || '',
                            cell.status || ''
                        ].join(','));
                    }
                });

                // Combine headers and rows
                const csvContent = [headers.join(','), ...rows].join('\n');

                // Create and download the file
                const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'});
                const link = document.createElement('a');
                const url = URL.createObjectURL(blob);
                link.setAttribute('href', url);
                link.setAttribute('download', `competency-matrix-${selectedPost?.name || 'Post'}-${new Date().toISOString().split('T')[0]}.csv`);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);

                // Show success message
                setInfoAlert({severity: 'success', message: 'CSV export completed successfully!'});
                setOpenSnackBar(true);
            } catch (error) {
                console.error('CSV export error:', error);
                setInfoAlert({severity: 'error', message: 'Failed to export CSV. Please try again.'});
                setOpenSnackBar(true);
            }
        }
    };

    const handlePostChange = (event) => {
        const value = event.target.value;
        setPosteIdForMatrix(value);
    };

    const handleUserToggle = (userId) => {
        setSelectedUserIds(prev =>
            prev.includes(userId)
                ? prev.filter(id => id !== userId)
                : [...prev, userId]
        );
    };

    const handleSelectAllUsers = () => {
        if (selectedPost?.users) {
            setSelectedUserIds(selectedPost.users.map(user => user.id));
        }
    };

    const handleDeselectAllUsers = () => {
        setSelectedUserIds([]);
    };

    // Filter users based on search term
    const filteredUsers = React.useMemo(() => {
        if (!selectedPost?.users) return [];

        if (!userSearchTerm) return selectedPost.users;

        if (showAcceptedOnly) return selectedPost.users;

        return selectedPost.users.filter(user =>
            (user.username || '').toLowerCase().includes(userSearchTerm.toLowerCase()) ||
            (user.email || '').toLowerCase().includes(userSearchTerm.toLowerCase()) ||
            String(user.id).includes(userSearchTerm)
        );
    }, [selectedPost?.users, userSearchTerm, showAcceptedOnly]);

    // CLIENT-SIDE FILTERING: Filter matrix data based on selected users
    const filteredMatrixData = React.useMemo(() => {
        if (!matrixData?.data || selectedUserIds.length === 0) {
            return matrixData?.data;
        }

        // If all users are selected, return full data
        if (selectedUserIds.length === selectedPost?.users?.length) {
            return matrixData.data;
        }

        // Filter employees and cells based on selected user IDs
        const filteredEmployees = matrixData.data.employees.filter(employee =>
            selectedUserIds.includes(employee.id)
        );

        const filteredCells = matrixData.data.cells.filter(cell =>
            selectedUserIds.includes(cell.employeeId)
        );

        return {
            ...matrixData.data,
            employees: filteredEmployees,
            cells: filteredCells
        };
    }, [matrixData?.data, selectedUserIds, selectedPost?.users?.length]);

    return (
        <Box p={3}>
            <div>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '2rem'
                }}>
                    <div>
                        <Typography sx={TitleScreen(colors)}>Quizs & assignments</Typography>
                        <Typography sx={SubTitleScreen(colors)}>
                            Generate and export skills matrix reports.
                        </Typography>
                    </div>
                </div>

                {/* Enhanced Filters Section */}
                <Paper
                    elevation={2}
                    sx={{
                        p: 3,
                        mb: 3,
                        backgroundColor: theme.palette.background.paper,
                        borderRadius: 2,
                        border: `1px solid ${colors.grey[300]}`
                    }}
                >
                    <Typography variant="h6" sx={{
                        mb: 2,
                        color: colors.grey[theme.palette.mode === 'dark' ? 800 : 700],
                        fontWeight: 600
                    }}>
                        Report Filters
                    </Typography>

                    <Stack direction={{xs: 'column', lg: 'row'}} spacing={3} alignItems="stretch">
                        {/* Post Selection */}
                        <Box sx={{minWidth: 280, flex: 1}}>
                            <FormControl fullWidth variant="outlined">
                                <InputLabel sx={{color: colors.grey[theme.palette.mode === 'dark' ? 700 : 600]}}>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                        <BusinessIcon fontSize="small"/>
                                        Select Position
                                    </Box>
                                </InputLabel>
                                <Select
                                    value={posteIdForMatrix}
                                    onChange={handlePostChange}
                                    label="Select Position"
                                    disabled={isLoadingPosts || isFetchingPosts}
                                    sx={{
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: colors.grey[400]
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: colors.primary[400]
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: colors.primary[300]
                                        }
                                    }}
                                >
                                    {postOptions.map((option) => (
                                        <MenuItem key={option.id} value={option.id}>
                                            <Box sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                width: '100%'
                                            }}>
                                                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                                    <Box
                                                        sx={{
                                                            width: 12,
                                                            height: 12,
                                                            borderRadius: '50%',
                                                            backgroundColor: option.color || colors.primary[400]
                                                        }}
                                                    />
                                                    <Typography>{option.title}</Typography>
                                                </Box>
                                                <Chip
                                                    label={`${option.userCount} users`}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: colors.green[400],
                                                        color: colors.grey[50],
                                                        fontSize: '0.7rem',
                                                        height: 20
                                                    }}
                                                />
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        {/* Export Buttons */}
                        <Box sx={{display: 'flex', gap: 1, flexShrink: 0}}>
                            <Button
                                variant="outlined"
                                onClick={() => handleExportMatrix('CSV')}
                                disabled={!matrixData}
                                startIcon={<GetAppIcon/>}
                                sx={{
                                    borderColor: colors.grey[400],
                                    color: colors.grey[theme.palette.mode === 'dark' ? 700 : 600],
                                    '&:hover': {
                                        borderColor: colors.primary[400],
                                        backgroundColor: colors.primary[50]
                                    },
                                    '&:disabled': {
                                        borderColor: colors.grey[300],
                                        color: colors.grey[500]
                                    }
                                }}
                            >
                                Export CSV
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => handleExportMatrix('PDF')}
                                disabled={!matrixData}
                                startIcon={<PictureAsPdfIcon/>}
                                sx={{
                                    backgroundColor: colors.redAccent[500],
                                    color: colors.grey[50],
                                    '&:hover': {
                                        backgroundColor: colors.redAccent[600]
                                    },
                                    '&:disabled': {
                                        backgroundColor: colors.grey[400],
                                        color: colors.grey[600]
                                    }
                                }}
                            >
                                Export PDF
                            </Button>
                        </Box>
                    </Stack>
                </Paper>

                {/* User Filter Section - Only show when post is selected */}
                {selectedPost && (
                    <Accordion
                        expanded={userListExpanded}
                        onChange={(event, isExpanded) => setUserListExpanded(isExpanded)}
                        sx={{
                            mb: 3,
                            backgroundColor: theme.palette.background.paper,
                            border: `1px solid ${colors.grey[300]}`,
                            borderRadius: '8px !important',
                            '&:before': {
                                display: 'none'
                            },
                            boxShadow: theme.shadows[1]
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon
                                sx={{color: colors.grey[theme.palette.mode === 'dark' ? 700 : 600]}}/>}
                            sx={{
                                borderRadius: '8px 8px 0 0',
                                '&.Mui-expanded': {
                                    borderRadius: '8px 8px 0 0'
                                }
                            }}
                        >
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                width: '100%',
                                mr: 1
                            }}>
                                <Typography variant="h6" sx={{
                                    color: colors.grey[700],
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}>
                                    <PersonIcon/>
                                    Filter Employees
                                </Typography>
                                <Chip
                                    label={`${selectedUserIds.length}/${selectedPost.users?.length || 0} selected`}
                                    size="small"
                                    sx={{
                                        backgroundColor: colors.primary[400],
                                        color: colors.grey[50]
                                    }}
                                />
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{p: 3}}>
                            <Box sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2}}>
                                <Box sx={{display: 'flex', gap: 1}}>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                        <Typography variant="body2" color="textSecondary">
                                            Accepted only
                                        </Typography>
                                        <Switch
                                            checked={showAcceptedOnly}
                                            onChange={(e) => setShowAcceptedOnly(e.target.checked)}
                                            color="success"
                                        />
                                    </Box>
                                    <Tooltip title="Select all users">
                                        <span>
                                            <IconButton
                                                size="small"
                                                onClick={handleSelectAllUsers}
                                                disabled={selectedUserIds.length === selectedPost.users?.length}
                                                sx={{
                                                    backgroundColor: colors.green[400],
                                                    color: colors.grey[50],
                                                    '&:hover': {
                                                        backgroundColor: colors.green[500]
                                                    },
                                                    '&:disabled': {
                                                        backgroundColor: colors.grey[400],
                                                        color: colors.grey[600]
                                                    }
                                                }}
                                            >
                                                <SelectAllIcon fontSize="small"/>
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title="Deselect all users">
                                        <span>
                                            <IconButton
                                                size="small"
                                                onClick={handleDeselectAllUsers}
                                                disabled={selectedUserIds.length === 0}
                                                sx={{
                                                    backgroundColor: colors.redAccent[500],
                                                    color: colors.grey[50],
                                                    '&:hover': {
                                                        backgroundColor: colors.redAccent[600]
                                                    },
                                                    '&:disabled': {
                                                        backgroundColor: colors.grey[400],
                                                        color: colors.grey[600]
                                                    }
                                                }}
                                            >
                                                <DeselectIcon fontSize="small"/>
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Box>
                            </Box>


                            {selectedPost.users && selectedPost.users.length > 0 ? (
                                <>
                                    {/* Search Field */}
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        placeholder="Search users by name, email, or ID..."
                                        value={userSearchTerm}
                                        onChange={(e) => setUserSearchTerm(e.target.value)}
                                        sx={{mb: 2}}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon sx={{color: colors.grey[500]}}/>
                                                </InputAdornment>
                                            ),
                                            sx: {
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: colors.grey[400]
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: colors.primary[400]
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: colors.primary[300]
                                                }
                                            }
                                        }}
                                    />

                                    {/* Users List */}
                                    <Box
                                        sx={{
                                            maxHeight: 250,
                                            overflow: 'auto',
                                            border: `1px solid ${colors.grey[300]}`,
                                            borderRadius: 1,
                                            backgroundColor: theme.palette.background.card
                                        }}
                                    >

                                        {isLoadingMatrixLoading || isFetchingMatrix ? (
                                            // ⏳ Loading state
                                            <Box sx={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                py: 3
                                            }}>
                                                <CircularProgress
                                                    size={24}
                                                    thickness={4}
                                                    sx={{color: colors.primary[400]}}
                                                />
                                            </Box>
                                        ) : matrixData?.data?.employees?.length === 0 ? (
                                            // ⚠️ No data case
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: colors.grey[500],
                                                    textAlign: 'center',
                                                    py: 2,
                                                    fontStyle: 'italic'
                                                }}
                                            >
                                                No users found for this post.
                                            </Typography>
                                        ) : (
                                            // ✅ Data available
                                            <List dense>
                                                {matrixData.data.employees.map((user, index) => (
                                                    <React.Fragment key={user.id}>
                                                        <ListItem disablePadding>
                                                            <ListItemButton
                                                                onClick={() => handleUserToggle(user.id)}
                                                                dense
                                                            >
                                                                <ListItemAvatar>
                                                                    <Avatar
                                                                        sx={{
                                                                            width: 32,
                                                                            height: 32,
                                                                            backgroundColor: colors.primary[400],
                                                                            color: colors.grey[50],
                                                                            fontSize: '0.8rem'
                                                                        }}
                                                                    >
                                                                        {(user.username || user.email)?.charAt(0).toUpperCase()}
                                                                    </Avatar>
                                                                </ListItemAvatar>
                                                                <ListItemText
                                                                    primary={
                                                                        <Typography
                                                                            sx={{
                                                                                color: colors.grey[theme.palette.mode === 'dark' ? 800 : 700],
                                                                                fontWeight: 500
                                                                            }}
                                                                        >
                                                                            {user.username || user.email}
                                                                        </Typography>
                                                                    }
                                                                    secondary={
                                                                        <Typography
                                                                            sx={{
                                                                                color: colors.grey[600],
                                                                                fontSize: '0.8rem'
                                                                            }}
                                                                        >
                                                                            ID: {user.id}
                                                                        </Typography>
                                                                    }
                                                                />
                                                                <Checkbox
                                                                    icon={<CheckBoxOutlineBlankIcon
                                                                        sx={{color: colors.grey[500]}}/>}
                                                                    checkedIcon={<CheckBoxIcon
                                                                        sx={{color: colors.green[400]}}/>}
                                                                    checked={selectedUserIds.includes(user.id)}
                                                                    tabIndex={-1}
                                                                    disableRipple
                                                                />
                                                            </ListItemButton>
                                                        </ListItem>
                                                        {index < matrixData.data.employees.length - 1 && (
                                                            <Divider sx={{backgroundColor: colors.grey[300]}}/>
                                                        )}
                                                    </React.Fragment>
                                                ))}
                                            </List>
                                        )}

                                    </Box>


                                </>
                            ) : (
                                <Typography variant="body2" sx={{color: colors.grey[500]}}>
                                    No users assigned to this post.
                                </Typography>
                            )}
                        </AccordionDetails>
                    </Accordion>
                )}

                {/* Competency Matrix */}
                <Card sx={{
                    p: 3,
                    mb: 3,
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: 2,
                    border: `1px solid ${colors.grey[300]}`
                }}>
                    <CardContent>
                        {isFetchingMatrix ? (
                            <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', my: 8}}>
                                <Box sx={{textAlign: 'center'}}>
                                    <CircularProgress size={48} sx={{color: colors.primary[400]}}/>
                                    <Typography sx={{mt: 2, color: colors.grey[600]}}>
                                        Loading competency matrix...
                                    </Typography>
                                </Box>
                            </Box>
                        ) : matrixError ? (
                            <Box sx={{textAlign: 'center', py: 4}}>
                                <Typography variant="h6" sx={{mb: 1, color: colors.redAccent[500]}}>
                                    Error Loading Matrix
                                </Typography>
                                <Typography variant="body2" sx={{color: colors.grey[600]}}>
                                    Please check your parameters and try again.
                                </Typography>
                            </Box>
                        ) : matrixData ? (
                            <>
                                <Typography variant="h6"
                                            sx={{mb: 2, color: colors.grey[theme.palette.mode === 'dark' ? 800 : 700]}}>
                                    Competency Matrix for {selectedPost?.name || 'Post'} #{matrixData.data?.posteId}
                                    {selectedUserIds.length > 0 && selectedUserIds.length < (selectedPost?.users?.length || 0) &&
                                        ` (${selectedUserIds.length} of ${selectedPost?.users?.length} users)`
                                    }
                                </Typography>
                                <CompetencyMatrix data={filteredMatrixData}/>
                            </>
                        ) : (
                            <Box sx={{textAlign: 'center', py: 8}}>
                                <BusinessIcon sx={{fontSize: 64, color: colors.grey[500], mb: 2}}/>
                                <Typography variant="h6"
                                            sx={{color: colors.grey[theme.palette.mode === 'dark' ? 700 : 600], mb: 1}}>
                                    Ready to Generate Matrix
                                </Typography>
                                <Typography variant="body2" sx={{color: colors.grey[600]}}>
                                    Select a post above to load the competency matrix.
                                </Typography>
                            </Box>
                        )}
                    </CardContent>
                </Card>

                {/* Export PDF dialog */}
                {selectedPost && (
                    <ReportPDFExport
                        open={exportOpen}
                        onClose={() => setExportOpen(false)}
                        matrixData={{...filteredMatrixData, postTitle: selectedPost?.name}}
                    />)}
            </div>
        </Box>
    );
};

export default CompetencyReportsDashboard;